// pages/DriversPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Typography,
  Stack,
  Skeleton,
  Tooltip,
} from "@mui/material";
import PageShell from "../components/PageShell";
import { useNavigate } from "react-router-dom";
import { ACC_FILES, AC_FILES, AC_EVO_FILES } from "../results/files.config";
import { getLicenseColor, getLicenseLabel } from "../utils/drivers.util";
import { buildLicenseMapFromSessions } from "../utils/driverLicenseStats";

/**
 * ===============================
 * Local cache for fetched JSON
 * ===============================
 * Cache Promise<json|null> per URL so the same file is never fetched twice
 * (and concurrent callers share the same in-flight request).
 */
const __jsonPromiseCache = new Map(); // url -> Promise<any|null>

async function fetchJSONUncached(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) {
      // file missing is fine; just skip
      return null;
    }

    let buf = await res.arrayBuffer();
    let u8 = new Uint8Array(buf);

    // Handle gzip when deployed (some hosts gzip static json)
    const isGzip = u8.length > 2 && u8[0] === 0x1f && u8[1] === 0x8b;
    if (isGzip) {
      try {
        if (typeof DecompressionStream === "function") {
          const ds = new DecompressionStream("gzip");
          const stream = new Response(buf).body.pipeThrough(ds);
          buf = await new Response(stream).arrayBuffer();
          u8 = new Uint8Array(buf);
        }
      } catch {
        return null;
      }
    }

    const tryDecode = (enc) => {
      try {
        return JSON.parse(new TextDecoder(enc, { fatal: false }).decode(u8));
      } catch {
        return null;
      }
    };

    return tryDecode("utf-8") || tryDecode("utf-16le") || tryDecode("utf-16be");
  } catch {
    return null;
  }
}

async function fetchJSON(url) {
  const key = String(url);

  if (__jsonPromiseCache.has(key)) {
    return __jsonPromiseCache.get(key);
  }

  const p = (async () => {
    const json = await fetchJSONUncached(key);
    return json;
  })();

  __jsonPromiseCache.set(key, p);

  p.catch(() => {
    __jsonPromiseCache.delete(key);
  });

  return p;
}

// ==============================
// ID helpers (match DriverProfilePage behavior)
// ==============================
const cleanId = (v) => {
  if (!v) return "";
  const str = String(v).trim();
  return str.replace(/^steam:/i, "").replace(/^S/i, "");
};
const keyOf = (id) => cleanId(id); // canonical key for maps
/**
 * Drivers page
 * Props: { data }
 */
export default function DriversPage({ data }) {
  const navigate = useNavigate();

  const intro =
    data?.drivers?.intro ??
    data?.driversIntro ??
    "Driver roster, numbers, teams, licenses, and pace charts.";

  const list = data?.drivers?.list ?? [];
  const standingsAC = data?.standings?.AC ?? {};
  const standingsACC = data?.standings?.ACC ?? {};

  const [licenseMap, setLicenseMap] = useState(() => new Map());
  const [loadingLicenses, setLoadingLicenses] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoadingLicenses(true);

      const toUrl = (f) =>
        process.env.PUBLIC_URL
          ? `${process.env.PUBLIC_URL}${f.startsWith("/") ? "" : "/"}${f}`
          : `${f.startsWith("/") ? "" : "/"}${f}`;

      const [accList, acList, aceList] = await Promise.all([
        Promise.all(ACC_FILES.map((f) => fetchJSON(toUrl(f)))),
        Promise.all(AC_FILES.map((f) => fetchJSON(toUrl(f)))),
        Promise.all(AC_EVO_FILES.map((f) => fetchJSON(toUrl(f)))),
      ]);

      if (!alive) return;

      const map = buildLicenseMapFromSessions({
        accSessions: accList,
        acSessions: acList,
        aceSessions: aceList,
      });

      setLicenseMap(map);
      setLoadingLicenses(false);
    })();

    return () => {
      alive = false;
    };
  }, []);

  const safeStandingsDriversAC = useMemo(
    () => (Array.isArray(standingsAC?.drivers) ? standingsAC.drivers : []),
    [standingsAC]
  );
  const safeStandingsDriversACC = useMemo(
    () => (Array.isArray(standingsACC?.drivers) ? standingsACC.drivers : []),
    [standingsACC]
  );

  const sortedDrivers = useMemo(() => {
    const copy = [...list];

    copy.sort((a, b) => {
      const aX = licenseMap.get(keyOf(a?.id))?.X ?? 0;
      const bX = licenseMap.get(keyOf(b?.id))?.X ?? 0;

      // Primary: license points desc
      if (bX !== aX) return bX - aX;

      // Tie-breaker: AC+ACC championship points desc (previous behavior)
      const aAC = safeStandingsDriversAC.find((dd) => dd.id === a.id)?.points || 0;
      const aACC = safeStandingsDriversACC.find((dd) => dd.id === a.id)?.points || 0;
      const bAC = safeStandingsDriversAC.find((dd) => dd.id === b.id)?.points || 0;
      const bACC = safeStandingsDriversACC.find((dd) => dd.id === b.id)?.points || 0;
      const diff = (bAC + bACC) - (aAC + aACC);
      if (diff !== 0) return diff;

      // Final tie-breaker: number then name
      const an = Number(a?.number) || 0;
      const bn = Number(b?.number) || 0;
      if (an !== bn) return an - bn;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });

    return copy;
  }, [list, licenseMap, safeStandingsDriversAC, safeStandingsDriversACC]);

  return (
    <PageShell title="Drivers" bannerImage="/f1.png">
      <Typography sx={{ mb: 3 }}>{intro}</Typography>

      {/* Driver Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {sortedDrivers.map((driver) => {
          const acPts = safeStandingsDriversAC.find((dd) => dd.id === driver.id)?.points ?? 0;
          const accPts = safeStandingsDriversACC.find((dd) => dd.id === driver.id)?.points ?? 0;

          const lic = licenseMap.get(keyOf(driver?.id));
          const X = lic?.X ?? 0;

          return (
            <Grid
              key={`${driver.number}-${driver.name}`}
              item
              xs={12}
              sm={6}
              md={4}
              onClick={() => navigate(`/driver/${driver.id}`)}
              sx={{ cursor: "pointer", width: { xs: "100%", md: "240px" } }}
            >
              <Card sx={{ height: "100%", width: "100%", display: "block" }}>
                <CardMedia component="img" image={driver.image} alt={driver.name} />
                <CardContent>
                  <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      #{driver.number}
                    </Typography>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip label={getLicenseLabel(X)} size="small" sx={{backgroundColor: getLicenseColor(getLicenseLabel(X))}} />
                      <Tooltip title="License Points = (Finishes × 5) + (Podiums × 5) + (Wins × 5)">
                        <Chip
                          label={
                            loadingLicenses ? (
                              <Skeleton width={42} />
                            ) : (
                              `LP ${X}`
                            )
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </Stack>
                  </Grid>

                  <Typography variant="h6">{driver.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {driver.team}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {driver.car}
                  </Typography>

                  <Typography variant="h6" color="primary.main" sx={{ mt: 1 }}>
                    {acPts} pts - AC
                  </Typography>
                  <Typography variant="h6" color="primary.main" sx={{ mt: 1 }}>
                    {accPts} pts - ACC
                  </Typography>

                  {!loadingLicenses && lic ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                      Fin {lic.finishes} · Pod {lic.podiums} · Wins {lic.wins}
                    </Typography>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </PageShell>
  );
}
